import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { CommonConstants } from '@coreConstant';

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  private lastErrorMessage: string | null = null;
  private lastErrorAt = 0;
  private static readonly DEDUPE_WINDOW_MS = 1500;

  constructor(private toastr: ToastrService, @Inject(PLATFORM_ID) private platformId: Object) { }

  /**
   * This method is used to show success toast notification
   * @param message
   */
  showSuccess(message: any) {
    if (isPlatformBrowser(this.platformId)) {
      const toastRef = this.toastr.success(message, '');
      this.clearToaster(toastRef);
    }
  }

  /**
   * Show an error toast. Accepts a string, HttpErrorResponse, an API response
   * body, or any error object. The real backend message is extracted when
   * available; otherwise CommonConstants.GENERIC_ERROR_MESSAGE is shown.
   *
   * Identical messages fired within DEDUPE_WINDOW_MS of each other are
   * suppressed, so the global interceptor and a downstream catchError block
   * that both call showError for the same HTTP failure produce one toast.
   */
  showError(message: any) {
    if (!isPlatformBrowser(this.platformId)) return;

    const errorMessage = this.extractErrorMessage(message);

    const now = Date.now();
    if (this.lastErrorMessage === errorMessage && now - this.lastErrorAt < ToastService.DEDUPE_WINDOW_MS) {
      return;
    }
    this.lastErrorMessage = errorMessage;
    this.lastErrorAt = now;

    const toastRef = this.toastr.error(errorMessage, '');
    this.clearToaster(toastRef);
  }

  /**
   * Centralized extraction of a user-facing message from any error shape the
   * backend or framework might hand us. Returns GENERIC_ERROR_MESSAGE when
   * nothing usable is found.
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      const trimmed = error.trim();
      return trimmed.length ? trimmed : CommonConstants.GENERIC_ERROR_MESSAGE;
    }

    if (!error || typeof error !== 'object') {
      return CommonConstants.GENERIC_ERROR_MESSAGE;
    }

    // For HttpErrorResponse the API payload is on .error; otherwise treat the
    // object itself as the body.
    const body = error instanceof HttpErrorResponse ? error.error : error;

    if (typeof body === 'string' && body.trim().length) {
      return body.trim();
    }

    const fromBody = this.findMessage(body);
    if (fromBody) return fromBody;

    // Some callers pass a plain response body without an HttpErrorResponse
    // wrapper — walk the outer object as a second pass when it differs.
    if (body !== error) {
      const fromOuter = this.findMessage(error);
      if (fromOuter) return fromOuter;
    }

    return CommonConstants.GENERIC_ERROR_MESSAGE;
  }

  /**
   * Walks a single object level looking for a message in any known API shape.
   * Returns null if nothing usable is found at this level.
   */
  private findMessage(obj: any): string | null {
    if (!obj || typeof obj !== 'object') return null;

    // Skip Angular's default HttpErrorResponse message
    // ("Http failure response for {url}: 0 Unknown Error") — it is never useful.
    if (this.isUsableString(obj.message) && !this.isHttpDefaultMessage(obj.message)) {
      return obj.message.trim();
    }

    for (const key of ['error_description', 'error_message', 'detail', 'title']) {
      if (this.isUsableString(obj[key])) return obj[key].trim();
    }

    // data: "string" | { message } | [{ message }]
    if (obj.data) {
      if (typeof obj.data === 'string' && obj.data.trim().length) {
        return obj.data.trim();
      }
      if (Array.isArray(obj.data) && obj.data.length) {
        const first = obj.data[0];
        if (typeof first === 'string' && first.trim().length) return first.trim();
        if (this.isUsableString(first?.message)) return first.message.trim();
      } else if (typeof obj.data === 'object' && this.isUsableString(obj.data.message)) {
        return obj.data.message.trim();
      }
    }

    // errors: ["msg"] | [{ message }] | { field: "msg" | ["msg"] }
    if (obj.errors) {
      if (Array.isArray(obj.errors) && obj.errors.length) {
        const first = obj.errors[0];
        if (typeof first === 'string' && first.trim().length) return first.trim();
        if (this.isUsableString(first?.message)) return first.message.trim();
      } else if (typeof obj.errors === 'object') {
        const firstKey = Object.keys(obj.errors)[0];
        const val = firstKey ? obj.errors[firstKey] : null;
        if (typeof val === 'string' && val.trim().length) return val.trim();
        if (Array.isArray(val) && typeof val[0] === 'string' && val[0].trim().length) return val[0].trim();
      }
    }

    // Inner .error wrapper (non-HttpErrorResponse shapes that still nest the body).
    if (obj.error && obj !== obj.error) {
      const nested = this.findMessage(obj.error);
      if (nested) return nested;
      if (typeof obj.error === 'string' && obj.error.trim().length) return obj.error.trim();
    }

    return null;
  }

  private isUsableString(value: any): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private isHttpDefaultMessage(msg: string): boolean {
    return /^http failure response/i.test(msg);
  }

  /**
   * Clears the toaster after it has been hidden
   */
  private clearToaster(toastRef: ActiveToast<any>) {
    if (!toastRef || !toastRef.toastId) return;
    setTimeout(() => {
      this.toastr.clear(toastRef.toastId);
    }, 3500); // Make sure this delay is slightly longer than your `timeOut`
  }

  showInfo(message: any) {
    const toastRef = this.toastr.info(message, '');
    this.clearToaster(toastRef);
  }
}
